import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiService } from '../../../services/api';
import type { PendingApprovalDetail } from '../../../services/api';
import { useToast } from '../../../contexts/ToastContext';
import { getApiErrorMessage } from '../../../types/api-errors';
import styles from './PendingApprovals.module.css';

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/** Backend requires a rejection reason of 10–1000 characters (RejectBusinessRequestValidator). */
const REJECT_REASON_MIN = 10;
const REJECT_REASON_MAX = 1000;

// AB#2108 (Admin V1 P0-2) — the founder approves pilot salons here, so this page
// reads the REAL backend detail (GetPendingApprovalDetailQuery): flat location,
// `submittedForReviewAt`, services with `categoryName` + `minDuration`
// ("hh:mm:ss"), and `bankDetails` / `license` omitted for pilot submissions (the
// API drops null properties). It previously read a nested shape that does not
// exist and crashed on every load. Admin pages stay English-only (W-17).

function formatDate(value?: string | null): string {
    if (!value) return '-';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString();
}

/**
 * `BusinessStatus` as the API serialises it (enum name), with the numeric enum as a
 * defensive fallback so a serializer change can never hide Approve from the founder.
 */
const STATUS_NAMES = ['Draft', 'PendingApproval', 'Approved', 'Rejected', 'Suspended'];
const STATUS_LABELS: Record<string, string> = {
    Draft: 'Draft',
    PendingApproval: 'Pending approval',
    Approved: 'Approved',
    Rejected: 'Rejected',
    Suspended: 'Suspended',
};

function statusName(status: unknown): string {
    if (typeof status === 'number') return STATUS_NAMES[status] ?? String(status);
    const raw = typeof status === 'string' ? status.trim() : '';
    return STATUS_NAMES.find((name) => name.toLowerCase() === raw.toLowerCase()) ?? raw;
}

/** Never show a raw enum name to the founder (localization gate, W-17 condition). */
function statusLabel(status: unknown): string {
    const name = statusName(status);
    return STATUS_LABELS[name] ?? (name || 'Unknown');
}

/**
 * Only a business that is actually awaiting review can be approved or rejected —
 * ApproveBusinessCommand / RejectBusinessCommand both require PendingApproval, so
 * offering the actions on any other status just produces a failed request.
 */
function isAwaitingReview(status: unknown): boolean {
    return statusName(status) === 'PendingApproval';
}

/** Keep the backend's explanation next to the action that failed (AB#1919 canonical reader). */
function actionFailure(error: unknown, action: string): string {
    const detail = getApiErrorMessage(error, '');
    return detail ? `${action}: ${detail}` : action;
}

/** TimeSpan "hh:mm:ss" (or "d.hh:mm:ss") → "N min". */
function formatDuration(value?: string | null): string {
    if (!value) return '-';
    const match = /^(?:(\d+)\.)?(\d{1,2}):(\d{2})(?::\d{2})?/.exec(value);
    if (!match) return value;
    const minutes = Number(match[1] ?? 0) * 1440 + Number(match[2]) * 60 + Number(match[3]);
    return `${minutes} min`;
}

const ApprovalDetailPage: React.FC = () => {
    const { businessId } = useParams<{ businessId: string }>();
    const navigate = useNavigate();
    const { showToast } = useToast();
    const [detail, setDetail] = useState<PendingApprovalDetail | null>(null);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);

    // Modal state
    const [showApproveModal, setShowApproveModal] = useState(false);
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [rejectReason, setRejectReason] = useState('');

    const fetchDetail = useCallback(async () => {
        if (!businessId) return;
        try {
            setLoading(true);
            const data = await apiService.getPendingApprovalDetail(Number(businessId));
            setDetail(data);
        } catch (error) {
            showToast(actionFailure(error, 'Failed to load business details'), 'error');
        } finally {
            setLoading(false);
        }
    }, [businessId, showToast]);

    useEffect(() => {
        fetchDetail();
    }, [fetchDetail]);

    const handleApprove = async () => {
        if (!businessId || !detail || !isAwaitingReview(detail.status)) return;
        try {
            setActionLoading(true);
            await apiService.approveBusiness(Number(businessId));
            showToast('Business approved successfully', 'success');
            navigate('/pending-approvals');
        } catch (error) {
            showToast(actionFailure(error, 'Failed to approve business'), 'error');
        } finally {
            setActionLoading(false);
            setShowApproveModal(false);
        }
    };

    const handleReject = async () => {
        if (!businessId || !detail || !isAwaitingReview(detail.status)) return;
        if (rejectReason.trim().length < REJECT_REASON_MIN) return;
        try {
            setActionLoading(true);
            await apiService.rejectBusiness(Number(businessId), rejectReason.trim());
            showToast('Business rejected', 'success');
            navigate('/pending-approvals');
        } catch (error) {
            showToast(actionFailure(error, 'Failed to reject business'), 'error');
        } finally {
            setActionLoading(false);
            setShowRejectModal(false);
        }
    };

    if (loading) {
        return (
            <div className={styles.detailContainer}>
                <div className={styles.loadingState}>
                    <div className="spinner" />
                    <span>Loading business details...</span>
                </div>
            </div>
        );
    }

    if (!detail) {
        return (
            <div className={styles.detailContainer}>
                <button className={styles.backLink} onClick={() => navigate('/pending-approvals')}>
                    &larr; Back to Pending Approvals
                </button>
                <div className={styles.emptyState}>Business not found.</div>
            </div>
        );
    }

    return (
        <div className={styles.detailContainer}>
            <button className={styles.backLink} onClick={() => navigate('/pending-approvals')}>
                &larr; Back to Pending Approvals
            </button>

            <h1 className={styles.title} style={{ marginBottom: 4 }}>{detail.businessName}</h1>
            <p className={styles.subtitle}>
                Submitted by {detail.ownerName} on {formatDate(detail.submittedForReviewAt)}
            </p>
            <p className={styles.subtitle}>Status: {statusLabel(detail.status)}</p>

            {/* Business Info */}
            <div className={styles.section}>
                <h3 className={styles.sectionTitle}>Business Information</h3>
                <div className={styles.fieldGrid}>
                    <div className={styles.field}>
                        <span className={styles.fieldLabel}>Business Name</span>
                        <span className={styles.fieldValue}>{detail.businessName}</span>
                    </div>
                    <div className={styles.field}>
                        <span className={styles.fieldLabel}>Type</span>
                        <span className={styles.fieldValue}>{detail.businessType || '-'}</span>
                    </div>
                    <div className={styles.field}>
                        <span className={styles.fieldLabel}>Service Type</span>
                        <span className={styles.fieldValue}>{detail.serviceType}</span>
                    </div>
                </div>
                {detail.about && (
                    <div className={styles.field} style={{ marginTop: 12 }}>
                        <span className={styles.fieldLabel}>About</span>
                        <span className={styles.fieldValue}>{detail.about}</span>
                    </div>
                )}
            </div>

            {/* Owner Info */}
            <div className={styles.section}>
                <h3 className={styles.sectionTitle}>Owner Profile</h3>
                <div className={styles.fieldGrid}>
                    <div className={styles.field}>
                        <span className={styles.fieldLabel}>Name</span>
                        <span className={styles.fieldValue}>{detail.ownerName}</span>
                    </div>
                </div>
            </div>

            {/* Location */}
            <div className={styles.section}>
                <h3 className={styles.sectionTitle}>Location</h3>
                <div className={styles.fieldGrid}>
                    <div className={styles.field}>
                        <span className={styles.fieldLabel}>Country</span>
                        <span className={styles.fieldValue}>{detail.country || '-'}</span>
                    </div>
                    <div className={styles.field}>
                        <span className={styles.fieldLabel}>City</span>
                        <span className={styles.fieldValue}>{detail.city || '-'}</span>
                    </div>
                    <div className={styles.field}>
                        <span className={styles.fieldLabel}>State</span>
                        <span className={styles.fieldValue}>{detail.state || '-'}</span>
                    </div>
                    <div className={styles.field}>
                        <span className={styles.fieldLabel}>Street Address</span>
                        <span className={styles.fieldValue}>{detail.streetAddress || '-'}</span>
                    </div>
                    <div className={styles.field}>
                        <span className={styles.fieldLabel}>Coordinates</span>
                        <span className={styles.fieldValue}>
                            {detail.latitude != null && detail.longitude != null
                                ? `${detail.latitude}, ${detail.longitude}`
                                : '-'}
                        </span>
                    </div>
                </div>
            </div>

            {/* Business Hours */}
            <div className={styles.section}>
                <h3 className={styles.sectionTitle}>Business Hours</h3>
                {detail.businessHours.length === 0 ? (
                    <span className={styles.fieldValue}>Not set</span>
                ) : (
                    <div className={styles.hoursGrid}>
                        {detail.businessHours.map((h) => (
                            <div key={h.dayOfWeek} className={styles.hourItem}>
                                <span style={{ fontWeight: 500 }}>{DAYS[h.dayOfWeek]}</span>
                                <span>
                                    {!h.isOpen ? 'Closed' : h.is24Hours ? 'Open 24 hours' : `${h.openTime ?? '-'} - ${h.closeTime ?? '-'}`}
                                </span>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Services */}
            <div className={styles.section}>
                <h3 className={styles.sectionTitle}>Services ({detail.services.length})</h3>
                <div className={styles.servicesList}>
                    {detail.services.map((s) => (
                        <div key={s.id} className={styles.serviceRow}>
                            <span>{s.name}</span>
                            <span style={{ color: 'var(--color-disabled)', fontSize: 13 }}>{s.categoryName || '-'}</span>
                            <span style={{ color: 'var(--color-disabled)', fontSize: 13 }}>{formatDuration(s.minDuration)}</span>
                            {/* No per-service currency in the DTO — show the amount only. */}
                            <span style={{ fontWeight: 600, marginInlineStart: 'auto' }}>{s.price != null ? s.price : '-'}</span>
                        </div>
                    ))}
                </div>
            </div>

            {/* Bank Details — omitted for controlled-pilot submissions (AB#2108). */}
            {detail.bankDetails && (
                <div className={styles.section}>
                    <h3 className={styles.sectionTitle}>Bank Details</h3>
                    <div className={styles.fieldGrid}>
                        <div className={styles.field}>
                            <span className={styles.fieldLabel}>Bank Name</span>
                            <span className={styles.fieldValue}>{detail.bankDetails.bankName}</span>
                        </div>
                        <div className={styles.field}>
                            <span className={styles.fieldLabel}>Account Holder</span>
                            <span className={styles.fieldValue}>{detail.bankDetails.accountHolderName}</span>
                        </div>
                        <div className={styles.field}>
                            <span className={styles.fieldLabel}>IBAN</span>
                            <span className={styles.fieldValue} dir="ltr">{detail.bankDetails.ibanMasked}</span>
                        </div>
                        <div className={styles.field}>
                            <span className={styles.fieldLabel}>Currency</span>
                            <span className={styles.fieldValue}>{detail.bankDetails.currency}</span>
                        </div>
                    </div>
                </div>
            )}

            {/* Licence & Documents — not required for controlled-pilot submissions (AB#2108). */}
            <div className={styles.section}>
                <h3 className={styles.sectionTitle}>Legal Documents</h3>
                {!detail.license && detail.documents.length === 0 ? (
                    <span className={styles.fieldValue}>None provided</span>
                ) : (
                    <div className={styles.fieldGrid}>
                        {detail.license && (
                            <>
                                <div className={styles.field}>
                                    <span className={styles.fieldLabel}>Trade License Number</span>
                                    <span className={styles.fieldValue} dir="ltr">{detail.license.licenseNumber}</span>
                                </div>
                                <div className={styles.field}>
                                    <span className={styles.fieldLabel}>Expiry Date</span>
                                    <span className={styles.fieldValue}>{formatDate(detail.license.expiryDate)}</span>
                                </div>
                                {detail.license.businessRegistrationNumber && (
                                    <div className={styles.field}>
                                        <span className={styles.fieldLabel}>Business Reg. Number</span>
                                        <span className={styles.fieldValue}>{detail.license.businessRegistrationNumber}</span>
                                    </div>
                                )}
                                {detail.license.taxRegistrationNumber && (
                                    <div className={styles.field}>
                                        <span className={styles.fieldLabel}>Tax Reg. Number</span>
                                        <span className={styles.fieldValue}>{detail.license.taxRegistrationNumber}</span>
                                    </div>
                                )}
                            </>
                        )}
                        {detail.documents.map((doc) => (
                            <div key={doc.id} className={styles.field}>
                                <span className={styles.fieldLabel}>{doc.type}</span>
                                <a
                                    href={doc.fileUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={styles.docLink}
                                >
                                    {doc.fileName}
                                </a>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Actions — only for a business that is actually awaiting review. */}
            {isAwaitingReview(detail.status) ? (
                <div className={styles.actions}>
                    <button
                        className={styles.approveBtn}
                        onClick={() => setShowApproveModal(true)}
                        disabled={actionLoading}
                    >
                        Approve
                    </button>
                    <button
                        className={styles.rejectBtn}
                        onClick={() => setShowRejectModal(true)}
                        disabled={actionLoading}
                    >
                        Reject
                    </button>
                </div>
            ) : (
                <div className={styles.section}>
                    <h3 className={styles.sectionTitle}>Review</h3>
                    <span className={styles.fieldValue}>
                        This business is not awaiting review ({statusLabel(detail.status)}), so it cannot be
                        approved or rejected here.
                    </span>
                </div>
            )}

            {/* Approve Confirmation Modal */}
            {showApproveModal && (
                <div className={styles.modalOverlay} onClick={() => setShowApproveModal(false)}>
                    <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                        <h3 className={styles.modalTitle}>Approve Business</h3>
                        <p className={styles.modalText}>
                            Are you sure you want to approve <strong>{detail.businessName}</strong>?
                            The owner will be notified and their salon will go live on the platform.
                        </p>
                        <div className={styles.modalActions}>
                            <button className={styles.modalCancel} onClick={() => setShowApproveModal(false)}>
                                Cancel
                            </button>
                            <button className={styles.approveBtn} onClick={handleApprove} disabled={actionLoading}>
                                {actionLoading ? 'Approving...' : 'Confirm Approve'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Reject Modal */}
            {showRejectModal && (
                <div className={styles.modalOverlay} onClick={() => setShowRejectModal(false)}>
                    <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                        <h3 className={styles.modalTitle}>Reject Business</h3>
                        <p className={styles.modalText}>
                            Please provide a reason for rejecting <strong>{detail.businessName}</strong>.
                            The owner will see this reason and can update and resubmit.
                        </p>
                        <textarea
                            className={styles.modalTextarea}
                            value={rejectReason}
                            onChange={(e) => setRejectReason(e.target.value)}
                            placeholder="Enter rejection reason..."
                            aria-label="Rejection reason"
                            aria-describedby="reject-reason-hint"
                            maxLength={REJECT_REASON_MAX}
                        />
                        <p id="reject-reason-hint" className={styles.modalText}>
                            At least {REJECT_REASON_MIN} characters. The owner sees this reason, so write it in Arabic and English if needed.
                        </p>
                        <div className={styles.modalActions}>
                            <button className={styles.modalCancel} onClick={() => setShowRejectModal(false)}>
                                Cancel
                            </button>
                            <button
                                className={styles.rejectBtn}
                                onClick={handleReject}
                                disabled={actionLoading || rejectReason.trim().length < REJECT_REASON_MIN}
                            >
                                {actionLoading ? 'Rejecting...' : 'Confirm Reject'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ApprovalDetailPage;
